"use client";

import { ChevronDown, ChevronUp, Heart } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ChangeEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import { useAuthDialogStore, useFavProductStore } from "@/features/user/ui/store";
import { AnalyticsEvents, trackEvent } from "@/lib/client/analytics/product-analytics";
import { useSession } from "@/lib/client/auth/client";
import { useCountryStore, useShoppingCartStore } from "@/lib/client/stores";
import type { ProductForClient, ProductVariantForClient } from "@/lib/shared/types/interfaces";
import { translateProductVariantLabel } from "@/lib/shared/utils/productVariantLabel";
import { Button, cn, HStack, Input, Label, RadioGroup, RadioGroupItem, Text, VStack } from "@/ui";

interface ProductVariantsProps {
  product: ProductForClient;
  selectedVariant: ProductVariantForClient;
  setSelectedVariant: (selectedVariant: ProductVariantForClient) => void;
}

export const ProductVariants = (props: ProductVariantsProps) => {
  const { product, selectedVariant, setSelectedVariant } = props;
  const generalT = useTranslations("general");
  const productDetailsT = useTranslations("product_details");
  const { setIsOpen, addItem } = useShoppingCartStore();
  const { data: session } = useSession();
  const { toggleAuthDialog } = useAuthDialogStore();
  const { favProducts, addFavProduct, removeFavProduct } = useFavProductStore();
  const [quantity, setQuantity] = useState<number>(1);
  const isFaved = useMemo(
    () => favProducts?.some((favProduct) => favProduct.id === product.id),
    [favProducts, product.id],
  );

  useEffect(() => {
    setQuantity(1);
  }, [selectedVariant.variant.id]);

  const { country } = useCountryStore();

  const onAddingToCart = () => {
    trackEvent(AnalyticsEvents.PRODUCT_ADDED_TO_CART, {
      product_id: product.id,
      product_name: product.name,
      sku: product.sku,
      variant_id: selectedVariant.variant.id,
      variant_name: selectedVariant.variant.variant,
      quantity,
      price: selectedVariant.onSalePrice || selectedVariant.price,
      currency: country.currencyCode,
    });
    addItem(product, selectedVariant.variant, quantity);
    setTimeout(() => {
      setIsOpen(true);
    }, 300);
  };

  const handleVariantChange = (variant: ProductVariantForClient) => {
    trackEvent(AnalyticsEvents.PRODUCT_VARIANT_SELECTED, {
      product_id: product.id,
      variant_id: variant.variant.id,
      variant_name: variant.variant.variant,
    });
    setSelectedVariant(variant);
  };

  const handleAddToWishlist = () => {
    if (session?.user) {
      trackEvent(AnalyticsEvents.PRODUCT_ADDED_TO_WISHLIST, {
        product_id: product.id,
        sku: product.sku,
      });
      addFavProduct(product);
    } else {
      toggleAuthDialog(true);
    }
  };

  const handleRemoveFromWishlist = () => {
    trackEvent(AnalyticsEvents.PRODUCT_REMOVED_FROM_WISHLIST, {
      product_id: product.id,
      sku: product.sku,
    });
    removeFavProduct(product);
  };

  const handleQuantityChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputQuantity = e.target.value;
    // Validate the input for a positive integer
    if (/^\d+$/.test(inputQuantity)) {
      setQuantity(Number.parseInt(inputQuantity));
    }
    if (!inputQuantity) {
      setQuantity(1);
    }
  };

  const handleQuantityBlur = (e: ChangeEvent<HTMLInputElement>) => {
    const inputQuantity = e.target.value;
    // Validate the input for a positive integer
    if (!/^\d+$/.test(inputQuantity)) {
      setQuantity(1);
    }
  };

  return (
    <VStack className="mt-6 space-y-6">
      {product.variants.length > 1 ? (
        <VStack className="space-y-2">
          <Text className="text-sm">{generalT("variants")}</Text>
          <RadioGroup
            defaultValue={selectedVariant.variant.variant}
            onValueChange={(value) => {
              // setSelectedVariant based on the name of the variant
              const productVariant = product.variants.find(
                (variant) => variant.variant.variant === value,
              );
              if (productVariant) {
                handleVariantChange(productVariant);
              } else if (product.variants[0]) {
                handleVariantChange(product.variants[0]);
              }
            }}
            className="mt-2 flex flex-wrap items-center"
          >
            {product.variants.map((variant) => (
              <HStack
                key={variant.variant.id}
                className={"items-center space-x-2"}
              >
                <RadioGroupItem
                  value={variant.variant.variant}
                  id={variant.variant.variant}
                />
                <Label
                  className={cn(
                    selectedVariant.variant.variant === variant.variant.variant
                      ? "rounded-md border-2 border-neutral px-2 py-1"
                      : "",
                    "flex items-center space-x-2 hover:cursor-pointer",
                  )}
                  htmlFor={variant.variant.variant}
                  onClick={() => handleVariantChange(variant)}
                >
                  <Text>
                    {translateProductVariantLabel(productDetailsT, variant.variant.variant)}
                  </Text>
                </Label>
              </HStack>
            ))}
          </RadioGroup>
        </VStack>
      ) : undefined}
      <VStack className="w-48 space-y-2">
        <Text className={"text-sm"}>{generalT("quantity")}: </Text>
        <HStack className={"join items-center"}>
          <Button
            className="join-item"
            disabled={!quantity || quantity <= 1}
            onClick={() => {
              setQuantity(quantity - 1);
            }}
          >
            <ChevronDown className={"h-6 w-6"} />
          </Button>
          <Input
            className="join-item text-center"
            placeholder={generalT("quantity")}
            value={quantity}
            onChange={handleQuantityChange}
            onBlur={handleQuantityBlur}
          />
          <Button
            className="join-item"
            onClick={() => {
              setQuantity(quantity + 1);
            }}
          >
            <ChevronUp className={"h-6 w-6"} />
          </Button>
        </HStack>
      </VStack>
      <HStack className={"mt-6 w-full gap-3"}>
        <Button
          className={cn(
            "flex-1 rounded-none py-5 text-[12px] font-medium uppercase tracking-[0.15em]",
            "transition-all duration-500",
            selectedVariant.stock === 0
              ? "bg-muted text-muted-foreground cursor-not-allowed"
              : "bg-foreground text-background hover:bg-primary hover:text-primary-foreground hover:shadow-gold",
          )}
          disabled={selectedVariant.stock === 0}
          data-testid="pdp-add-to-cart"
          onClick={onAddingToCart}
        >
          {selectedVariant.stock === 0 ? generalT("out_of_stock") : generalT("add_to_cart")}
        </Button>
        {isFaved ? (
          <Button
            variant={"ghost"}
            className="ml-4"
            onClick={handleRemoveFromWishlist}
          >
            <Heart className={"h-6 w-6 flex-shrink-0 fill-red-500 text-red-500"} />
          </Button>
        ) : (
          <Button
            variant={"ghost"}
            className="ml-4"
            onClick={handleAddToWishlist}
          >
            <Heart className={"h-6 w-6 flex-shrink-0"} />
          </Button>
        )}
      </HStack>
    </VStack>
  );
};
